'use client';

import { useCallback } from 'react';
import ImageWithFallback from '@/components/common/ImageWithFallback';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

import { Navigation } from 'swiper/modules';
import { ContentShelf } from '@/context/global/types/homeShelf';
import CWSectionHeader from '@/context/global/components/g02/g02-d01/g02-d01-g002-cw-section-header';
import { useRouter } from 'next/navigation';

export default function CWSongCategoriesSection({ shelf }: { shelf: ContentShelf }) {
  const router = useRouter();

  const handleCategoryClick = useCallback(
    (categoryId: number) => {
      router.push(`/category-detail/${categoryId}`);
    },
    [router],
  );

  return (
    <section className="w-full">
      <CWSectionHeader title={shelf.title} />
      <div className="w-full">
        <Swiper
          className="w-full overflow-visible"
          slidesPerView={1.5}
          spaceBetween={8}
          slidesOffsetBefore={8}
          slidesOffsetAfter={8}
          navigation={true}
          modules={[Navigation]}
          breakpoints={{
            320: {
              slidesPerView: 2.2,
              spaceBetween: 10,
              slidesOffsetBefore: 8,
              slidesOffsetAfter: 8,
            },

            414: {
              slidesPerView: 2.5,
              spaceBetween: 10,
              slidesOffsetBefore: 8,
              slidesOffsetAfter: 8,
            },
            454: {
              slidesPerView: 2.8,
              spaceBetween: 10,
              slidesOffsetBefore: 8,
              slidesOffsetAfter: 8,
            },
            500: {
              slidesPerView: 2.8,
              spaceBetween: 10,
              slidesOffsetBefore: 8,
              slidesOffsetAfter: 8,
            },
            640: {
              slidesPerView: 3.2,
              spaceBetween: 10,
              slidesOffsetBefore: 8,
              slidesOffsetAfter: 8,
            },
            768: {
              slidesPerView: 4.5,
              spaceBetween: 10,
            },
            820: {
              slidesPerView: 4.8,
              spaceBetween: 10,
            },
            1024: {
              slidesPerView: 5,
              spaceBetween: 10,
            },
            1248: {
              slidesPerView: 6,
              spaceBetween: 10,
            },
            1280: {
              slidesPerView: 5.2,
              spaceBetween: 10,
            },
            1350: {
              slidesPerView: 5.5,
              spaceBetween: 10,
            },
            1500: {
              slidesPerView: 6,
              spaceBetween: 10,
            },
            1600: {
              slidesPerView: 6.4,
              spaceBetween: 10,
            },
            1720: {
              slidesPerView: 6.8,
              spaceBetween: 10,
            },
            1920: {
              slidesPerView: 7.5,
              spaceBetween: 10,
            },
            2048: {
              slidesPerView: 8.2,
              spaceBetween: 14,
            },
          }}
        >
          {shelf.items.map((cat, i) => (
            <SwiperSlide
              key={`category-slide-${i}-${cat.id ?? 'no-id'}`}
              className="ml-0.5 xl:ml-0"
            >
              <div className="relative aspect-[5/3] w-[150px] pl-0.5 sm:pl-1 md:w-[160px] lg:w-[180px] lg:pl-2 xl:w-[220px]">
                <div
                  className="relative inset-0 h-full w-full cursor-pointer rounded-[8px] bg-cover bg-center transition-all duration-200 hover:ring-2 hover:ring-white sm:rounded-[10px]"
                  style={{
                    backgroundImage: `url('${cat.coverUrl}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                  onClick={() => handleCategoryClick(cat.id)}
                >
                  <ImageWithFallback
                    src={cat.coverUrl}
                    alt={cat.title || 'cover'}
                    className="h-full w-full rounded-[6px] object-cover sm:rounded-[8px]"
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}
